import React, { useState, useEffect } from "react";
import { MdPerson, MdFilterList } from "react-icons/md";
import UserServiceInstance from "service/users.service";
import RoleServiceInstance from "service/role.service";
import PromotionServiceInstance from "service/promotion.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import UserTable from "./components/UserTable";
import UserFormModal from "./components/UserFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";
import UserFilters from "./components/UserFilters";
import UserDetailModal from "./components/UserDetailModal";

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    nom_prenom_membre: "",
    id_prom: "",
    tel_membre: "",
    com_origin: "",
    num_matricule: "",
    adresse_membre: "",
    date_naissance: new Date().toISOString().split("T")[0],
    username: "",
    cin_membre: "",
    password: "",
    email: "",
    photo_profile: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    promotion: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPromotions();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, users, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await UserServiceInstance.getAll();
      setUsers(result || []);
      setFilteredUsers(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les utilisateurs. Veuillez réessayer plus tard."
      );
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const result = await RoleServiceInstance.getAll();
      setRoles(result || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchPromotions = async () => {
    setPromotionsLoading(true);
    try {
      const result = await PromotionServiceInstance.getAll();
      setPromotions(result || []);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    } finally {
      setPromotionsLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Recherche textuelle
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (user.username && user.username.toLowerCase().includes(search)) ||
          (user.email && user.email.toLowerCase().includes(search)) ||
          (user.membre &&
            user.membre.nom_prenom_membre &&
            user.membre.nom_prenom_membre.toLowerCase().includes(search))
      );
    }

    // Filtre par rôle
    if (filters.role) {
      filtered = filtered.filter(
        (user) => user.role && user.role.role_id.toString() === filters.role
      );
    }

    // Filtre par statut
    if (filters.status) {
      const isActive = filters.status === "active";
      filtered = filtered.filter((user) => user.isActive === isActive);
    }

    // Filtre par promotion
    if (filters.promotion) {
      filtered = filtered.filter(
        (user) =>
          user.membre &&
          user.membre.promotion &&
          user.membre.promotion.id_prom.toString() === filters.promotion
      );
    }

    setFilteredUsers(filtered);
  };

  const openCreateModal = () => {
    setCurrentUser({
      nom_prenom_membre: "",
      id_prom: "",
      tel_membre: "",
      com_origin: "",
      num_matricule: "",
      adresse_membre: "",
      date_naissance: new Date().toISOString().split("T")[0],
      username: "",
      cin_membre: "",
      password: "",
      email: "",
      photo_profile: null,
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (user) => {
    setCurrentUser({
      user_id: user.user_id,
      username: user.username,
      email: user.email,
      photo: user.photo,
      isActive: user.isActive,
      role_id: user.role ? user.role.role_id : "",
      nom_prenom_membre: user.membre ? user.membre.nom_prenom_membre : "",
      id_prom:
        user.membre && user.membre.promotion
          ? user.membre.promotion.id_prom
          : "",
      tel_membre: user.membre ? user.membre.tel_membre : "",
      com_origin: user.membre ? user.membre.com_origin : "",
      num_matricule: user.membre ? user.membre.num_matricule : "",
      adresse_membre: user.membre ? user.membre.adresse_membre : "",
      date_naissance:
        user.membre && user.membre.date_naissance
          ? user.membre.date_naissance
          : "",
      cin_membre: user.membre ? user.membre.cin_membre : "",
      // Ne jamais inclure le mot de passe lors de l'édition
      password: "",
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (user) => {
    setCurrentUser(user);
    setIsDeleteModalOpen(true);
  };

  const openDetailModal = (user) => {
    setCurrentUser(user);
    setIsDetailModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      const data = new FormData();

      // Ajouter tous les champs au formData
      Object.keys(formData).forEach((key) => {
        if (key !== "photo_profile" || formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });

      if (isEditing) {
        await UserServiceInstance.update(currentUser.user_id, data);
      } else {
        await UserServiceInstance.create(data);
      }

      fetchUsers();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier l'utilisateur. Veuillez réessayer."
            : "Impossible de créer l'utilisateur. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await UserServiceInstance.delete(currentUser.user_id);
      fetchUsers();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer l'utilisateur. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = async (userId, activate = true) => {
    setLoading(true);
    try {
      if (activate) {
        await UserServiceInstance.activate(userId);
      } else {
        await UserServiceInstance.deactivate(userId);
      }
      fetchUsers();
      setIsDetailModalOpen(false);
    } catch (error) {
      setError(
        `Impossible de ${
          activate ? "activer" : "désactiver"
        } l'utilisateur. Veuillez réessayer.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAvatar = async (userId, file) => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("photo_profile", file);

      await UserServiceInstance.updateAvatar(userId, formData);
      fetchUsers();
      return { success: true };
    } catch (error) {
      setError(
        "Impossible de mettre à jour la photo de profil. Veuillez réessayer."
      );
      return {
        success: false,
        error: "Impossible de mettre à jour la photo de profil.",
      };
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const resetFilters = () => {
    setFilters({
      role: "",
      status: "",
      promotion: "",
    });
    setSearchTerm("");
  };

  // Calculer les statistiques
  const calculerStats = () => {
    const totalUsers = filteredUsers.length;
    const activeUsers = filteredUsers.filter((user) => user.isActive).length;
    const inactiveUsers = totalUsers - activeUsers;

    // Calculer la distribution par rôles
    const roleDistribution = roles.map((role) => {
      const count = filteredUsers.filter(
        (user) => user.role && user.role.role_id === role.role_id
      ).length;

      return {
        role_name: role.role_name,
        count,
      };
    });

    return {
      totalUsers,
      activeUsers,
      inactiveUsers,
      roleDistribution,
    };
  };

  const stats = calculerStats();

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Utilisateurs"
        description="Gérez les comptes utilisateurs et leurs droits d'accès"
        accentColor="#6366F1"
      />

      {/* Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4">
          <h3 className="text-sm font-medium text-indigo-900">
            Total des utilisateurs
          </h3>
          <p className="mt-2 text-xl font-bold text-indigo-700">
            {stats.totalUsers}
          </p>
        </div>

        <div className="rounded-md border border-green-200 bg-green-50 p-4">
          <h3 className="text-sm font-medium text-green-900">Comptes actifs</h3>
          <p className="mt-2 text-xl font-bold text-green-700">
            {stats.activeUsers}
          </p>
        </div>

        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <h3 className="text-sm font-medium text-red-900">Comptes inactifs</h3>
          <p className="mt-2 text-xl font-bold text-red-700">
            {stats.inactiveUsers}
          </p>
        </div>

        <div className="rounded-md border border-purple-200 bg-purple-50 p-4">
          <h3 className="text-sm font-medium text-purple-900">Rôles</h3>
          <div className="mt-2">
            {stats.roleDistribution.map(
              (item, index) =>
                item.count > 0 && (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-purple-700">{item.role_name}</span>
                    <span className="font-medium text-purple-900">
                      {item.count}
                    </span>
                  </div>
                )
            )}
          </div>
        </div>
      </div>

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Rechercher par nom, username ou email..."
        searchIcon={MdPerson}
        onRefresh={fetchUsers}
        onCreateNew={openCreateModal}
        buttonLabel="Nouvel Utilisateur"
        loading={loading}
        primaryColor="#6366F1"
        hoverColor="#4F46E5"
        customActions={
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-200"
          >
            <MdFilterList className="h-5 w-5" />
            <span>Filtres</span>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs">
              {Object.values(filters).filter((v) => v !== "").length}
            </span>
          </button>
        }
      />

      {showFilters && (
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          roles={roles}
          promotions={promotions}
        />
      )}

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <UserTable
        users={filteredUsers}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onView={openDetailModal}
        onActivate={(userId) => handleActivateUser(userId, true)}
        onDeactivate={(userId) => handleActivateUser(userId, false)}
      />

      <UserFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialUser={currentUser}
        isEditing={isEditing}
        roles={roles}
        promotions={promotions}
        loading={loading}
        rolesLoading={rolesLoading}
        promotionsLoading={promotionsLoading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentUser}
        itemName={
          currentUser.username || currentUser.email || "cet utilisateur"
        }
        itemType="utilisateur"
        warning="La suppression d'un compte utilisateur est irréversible et supprimera également toutes les données associées."
        loading={loading}
      />

      <UserDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        user={currentUser}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openEditModal(currentUser), 300);
        }}
        onDelete={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openDeleteModal(currentUser), 300);
        }}
        onActivate={() => handleActivateUser(currentUser.user_id, true)}
        onDeactivate={() => handleActivateUser(currentUser.user_id, false)}
        onUpdateAvatar={(file) => handleUpdateAvatar(currentUser.user_id, file)}
        loading={loading}
      />
    </div>
  );
};

export default UserManager;
